"use client";

import type { SimBalanceItem } from "@/hooks/useSimTokenInfo";
import type { Connector, ConnectorType } from "@/stores/connectorsStore";
import type { Order } from "@/stores/ordersStore";
import type { Template } from "@/stores/templatesStore";
import type { Workflow, WorkflowNode } from "@/stores/workflowsStore";
import type { WorkflowRun } from "@/stores/workflowRunsStore";
import type { OrganizationInvite, OrganizationMember } from "@/types/organization";
import type { OrganizationWallet } from "@/types/wallet";

export function compactWorkflow(workflow: Workflow) {
  return {
    id: workflow.id,
    name: workflow.name,
    description: workflow.description,
    status: workflow.status,
    version: workflow.version,
    visibility: workflow.visibility,
    triggerType: workflow.triggerType,
    triggerName: workflow.triggerName,
    nodeCount: workflow.nodeCount ?? Object.keys(workflow.definition?.nodes ?? {}).length,
    hasDraft: workflow.hasDraft,
    hasLiveVersion: workflow.hasLiveVersion,
    publicWidgetEnabled: workflow.publicWidgetEnabled,
    remainRuns: workflow.remainRuns,
    updatedAt: workflow.updatedAt,
    createdAt: workflow.createdAt,
  };
}

export function compactRun(run: WorkflowRun) {
  return {
    id: run.id,
    workflowId: run.workflowId,
    workflowVersion: run.workflowVersion,
    status: run.status,
    triggerSource: run.triggerSource,
    totalCu: run.totalCu,
    startedAt: run.startedAt,
    finishedAt: run.finishedAt,
    isSimulation: run.isSimulation,
    isDustTest: run.isDustTest,
  };
}

export function compactConnector(connector: Connector) {
  return {
    id: connector.id,
    type: connector.type,
    name: connector.name,
    description: connector.description,
    createdAt: connector.createdAt,
    updatedAt: connector.updatedAt,
    maskedData: connector.maskedData,
  };
}

export function compactConnectorType(connectorType: ConnectorType) {
  return {
    type: connectorType.type,
    name: connectorType.name,
    description: connectorType.description,
    provider: connectorType.provider,
    providerAuthType: connectorType.providerConfig?.authType,
    optional: connectorType.optional,
    requiredFields: connectorType.dataSchema.required ?? [],
    tags: connectorType.tags,
  };
}

export function compactTemplate(template: Template) {
  return {
    id: template.id,
    slug: template.slug,
    name: template.name,
    description: template.description,
    visibility: template.visibility,
    tags: template.tags?.map(tag => tag.name || tag.slug),
    isPromoted: template.isPromoted,
    forkCount: template.forkCount,
    nodeCount: Object.keys(template.definition?.nodes ?? {}).length,
    propCount: template.templateProps?.length ?? 0,
    requiredPropCount: template.templateProps?.filter(prop => prop.required && prop.default === undefined).length ?? 0,
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
  };
}

export function compactOrder(order: Order) {
  return {
    id: order.id,
    label: order.label,
    orderType: order.orderType,
    status: order.status,
    chainId: order.chainId,
    assetAddress: order.assetAddress,
    amount: order.amount,
    targetPriceUsd: order.targetPriceUsd,
    currentPriceUsd: order.currentPriceUsd,
    groupId: order.groupId,
    priceStale: order.priceStale,
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
  };
}

export function compactNode(nodeId: string, node: WorkflowNode) {
  return {
    id: nodeId,
    type: node.type,
    title: node.titleOverride,
    description: node.description,
    connector: node.connector,
    childCount: node.children?.length ?? 0,
    branch: node.branch,
    payloadKeys: Object.keys(node.payload ?? {}),
  };
}

export function compactWallet(wallet: OrganizationWallet) {
  return {
    id: wallet.id,
    name: wallet.name,
    address: wallet.address,
    isDefault: wallet.isDefault,
    isImported: wallet.isImported,
    providerType: wallet.providerType,
    accountType: wallet.accountType,
    createdAt: wallet.createdAt,
    updatedAt: wallet.updatedAt,
  };
}

export function compactBalance(balance: SimBalanceItem) {
  return {
    chain: balance.chain,
    chainId: balance.chain_id,
    tokenAddress: balance.address,
    symbol: balance.symbol,
    name: balance.name,
    amount: balance.amount,
    decimals: balance.decimals,
    priceUsd: balance.price_usd,
    valueUsd: balance.value_usd,
    lowLiquidity: balance.low_liquidity,
  };
}

export function compactOrganizationMember(member: OrganizationMember) {
  return {
    id: member.id,
    userId: member.userId,
    role: member.role,
    userEmail: member.userEmail,
    userName: member.userName,
    createdAt: member.createdAt,
    updatedAt: member.updatedAt,
  };
}

export function compactOrganizationInvite(invite: OrganizationInvite) {
  return {
    id: invite.id,
    inviteeEmail: invite.inviteeEmail,
    inviteeUserId: invite.inviteeUserId,
    role: invite.role,
    status: invite.status,
    expiresAt: invite.expiresAt,
    createdAt: invite.createdAt,
    updatedAt: invite.updatedAt,
  };
}
